import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BidsReceivedCard from "../components/BidsReceivedCard";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  LayoutDashboard, Plus, Briefcase, MessageSquare, Clock, MapPin,
  DollarSign, Eye, Trash2, CheckCircle2, AlertCircle, Loader2, Smartphone, Zap
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ total_jobs: 0, open_jobs: 0, total_responses: 0 });
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    try {
      const [jobsRes, statsRes] = await Promise.all([
        axios.get(`${API}/jobs/my`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/stats/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setJobs(jobsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteJob = async (jobId) => {
    try {
      await axios.delete(`${API}/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: "bg-green-100 text-green-700",
      in_progress: "bg-yellow-100 text-yellow-700",
      completed: "bg-blue-100 text-blue-700",
      cancelled: "bg-gray-100 text-gray-700"
    };
    return <Badge className={styles[status] || styles.open}>{status.replace("_", " ")}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    if (urgency === "urgent") {
      return <Badge className="bg-red-100 text-red-700">Urgent</Badge>;
    }
    return null;
  };

  // Check if job is stale (>24h with no responses and bidding not enabled)
  const isStaleJob = (job) => {
    if (job.bidding_enabled || job.responses_count > 0 || job.status !== "open") return false;
    const createdAt = new Date(job.created_at);
    const now = new Date();
    const hoursOld = (now - createdAt) / (1000 * 60 * 60);
    return hoursOld > 24;
  };

  const handleEnableBidding = async (jobId) => {
    try {
      await axios.put(
        `${API}/jobs/${jobId}/enable-bidding`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Express Bidding enabled! You'll receive competitive bids from up to 3 professionals.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enable bidding");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto" data-testid="customer-dashboard">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mb-2">
            Welcome back, {user?.full_name?.split(" ")[0]}!
          </h1>
          <p className="text-gray-600">Manage your job posts and view responses from professionals.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="border-stone-200" data-testid="stat-total-jobs">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-forest" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_jobs}</p>
                  <p className="text-sm text-gray-500">Total Jobs Posted</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200" data-testid="stat-open-jobs">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.open_jobs}</p>
                  <p className="text-sm text-gray-500">Active Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200" data-testid="stat-total-responses">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-sienna/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-sienna" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_responses}</p>
                  <p className="text-sm text-gray-500">Total Responses</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile App Banner */}
        <Card className="border-0 bg-gradient-to-r from-slate-900 to-slate-800 text-white mb-8" data-testid="mobile-app-banner">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">BQL Mobile App - Coming Soon!</h3>
                  <p className="text-white/70 text-sm">We're building native iOS & Android apps for you</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-2 rounded-lg text-sm font-medium border border-white/20">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  iOS Soon
                </span>
                <span className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-2 rounded-lg text-sm font-medium border border-white/20">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  Android Soon
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Button */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading font-semibold text-xl text-gray-900">Your Jobs</h2>
          <Link to="/post-job">
            <Button className="bg-forest hover:bg-forest-dark rounded-full" data-testid="post-new-job-btn">
              <Plus className="w-4 h-4 mr-2" />
              Post New Job
            </Button>
          </Link>
        </div>

        {/* Jobs List */}
        {jobs.length === 0 ? (
          <Card className="border-stone-200 border-dashed" data-testid="no-jobs-message">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">No jobs posted yet</h3>
              <p className="text-gray-500 mb-6">Post your first job to start receiving quotes from professionals.</p>
              <Link to="/post-job">
                <Button className="bg-forest hover:bg-forest-dark rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Post Your First Job
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Card key={job.id} className="border-stone-200 hover:shadow-md transition-shadow" data-testid={`job-card-${job.id}`}>
                <CardContent className="p-6">
                  {/* Stale Job Banner - Enable Bidding CTA */}
                  {isStaleJob(job) && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl" data-testid={`stale-job-banner-${job.id}`}>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-amber-900">No responses yet?</p>
                            <p className="text-sm text-amber-700">Enable Express Bidding to get up to 3 competitive bids fast!</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-full whitespace-nowrap"
                          onClick={() => handleEnableBidding(job.id)}
                          data-testid={`enable-bidding-${job.id}`}
                        >
                          <Zap className="w-4 h-4 mr-1" />
                          Enable Bidding
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(job.status)}
                        {getUrgencyBadge(job.urgency)}
                        {job.bidding_enabled && (
                          <Badge className="bg-blue-100 text-blue-700" data-testid={`bidding-badge-${job.id}`}>
                            <Zap className="w-3 h-3 mr-1" />
                            Bidding
                          </Badge>
                        )}
                        {job.urgency === "emergency" && (
                          <Badge className="bg-purple-100 text-purple-700">
                            <Zap className="w-3 h-3 mr-1" />
                            {job.bid_count || 0}/{job.max_bids || 3} bids
                          </Badge>
                        )}
                        {job.pricing_type === "target_price" && job.target_price && (
                          <Badge className="bg-green-100 text-green-700">
                            🎯 Target: ${job.target_price}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">{job.title}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-3">{job.description}</p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        {(job.budget_min || job.budget_max) && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {job.budget_min && job.budget_max
                              ? `$${job.budget_min} - $${job.budget_max}`
                              : job.budget_max ? `Up to $${job.budget_max}` : `From $${job.budget_min}`}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(job.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {job.responses_count} responses
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Link to={`/job/${job.id}`}>
                        <Button variant="outline" size="sm" className="rounded-full" data-testid={`view-job-${job.id}`}>
                          <Eye className="w-4 h-4 mr-1" />
                          View Bids
                        </Button>
                      </Link>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-job-${job.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. All responses will also be deleted.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteJob(job.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
